import React, { useState, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Slider } from './ui/slider';
import { Button } from './ui/button';
import { ArrowRight, Banknote, DollarSign, TrendingUp, Calendar } from 'lucide-react';

const WHATSAPP_LINK = "https://wa.me/529841585732?text=Hola%2C%20vi%20la%20p%C3%A1gina%20de%20Marea%20Riviera%20y%20quiero%20una%20asesor%C3%ADa%20sin%20costo.";

export const CashFlowSimulator = () => {
  const { language } = useLanguage();

  const [nightlyRate, setNightlyRate] = useState([200]);
  const [occupancy, setOccupancy] = useState([75]);
  const [operatingCost, setOperatingCost] = useState([30]);

  const calculations = useMemo(() => {
    const monthlyNights = 30;
    const occupiedNights = monthlyNights * (occupancy[0] / 100);
    const grossMonthly = nightlyRate[0] * occupiedNights;
    const expenses = grossMonthly * (operatingCost[0] / 100);
    const netMonthly = grossMonthly - expenses;
    const netAnnual = netMonthly * 12;
    const grossAnnual = grossMonthly * 12;

    return {
      grossMonthly: Math.round(grossMonthly),
      netMonthly: Math.round(netMonthly),
      netAnnual: Math.round(netAnnual),
      grossAnnual: Math.round(grossAnnual),
    };
  }, [nightlyRate, occupancy, operatingCost]);

  const formatCurrency = (value) => {
    const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
    const mxnVal = Math.round(value * 20);
    const mxn = `$${mxnVal.toLocaleString('es-MX')} MXN`;
    return { usd, mxn };
  };

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="bg-[#D4EDE8] py-16 md:py-[60px]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0A2235] text-[#2BBFBF] rounded-[20px] mb-6">
            <Banknote className="w-8 h-8" />
          </div>
          <h2
            className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#0A2235] mb-4 tracking-tight"
            data-testid="calculator-section-title"
          >
            {language === 'es' ? '¿Cuánto ingreso mensual genera tu propiedad?' : 'How much monthly income does your property generate?'}
          </h2>
          <p className="font-inter text-[#5F5E5A] text-base sm:text-lg font-light">
            {language === 'es' ? 'Simula tu cash flow neto en segundos' : 'Simulate your net cash flow in seconds'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Sliders */}
          <div className="bg-white p-8 md:p-12 shadow-sm space-y-10 rounded-[20px]">
            {/* Nightly Rate */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="font-inter text-sm text-[#0A2235]/70 uppercase tracking-widest">
                  {language === 'es' ? 'Tarifa por noche' : 'Nightly rate'}
                </label>
                <span className="font-inter font-medium text-[#0A2235] text-lg">
                  {formatCurrency(nightlyRate[0]).usd}
                </span>
              </div>
              <Slider
                value={nightlyRate}
                onValueChange={setNightlyRate}
                min={80}
                max={500}
                step={10}
                className="[&_[role=slider]]:bg-[#0A2235] [&_[role=slider]]:border-[#0A2235] [&_.relative]:bg-[#0A2235]/20 [&_[data-orientation=horizontal]>[data-orientation=horizontal]]:bg-[#2BBFBF]"
                data-testid="cf-slider-nightly"
              />
              <div className="flex justify-between text-xs text-[#0A2235]/50 font-inter">
                <span>$80</span>
                <span>$500</span>
              </div>
            </div>

            {/* Occupancy */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="font-inter text-sm text-[#0A2235]/70 uppercase tracking-widest">
                  {language === 'es' ? 'Ocupación mensual' : 'Monthly occupancy'}
                </label>
                <span className="font-inter font-medium text-[#0A2235] text-lg">
                  {occupancy[0]}%
                </span>
              </div>
              <Slider
                value={occupancy}
                onValueChange={setOccupancy}
                min={50}
                max={95}
                step={1}
                className="[&_[role=slider]]:bg-[#0A2235] [&_[role=slider]]:border-[#0A2235] [&_.relative]:bg-[#0A2235]/20 [&_[data-orientation=horizontal]>[data-orientation=horizontal]]:bg-[#2BBFBF]"
                data-testid="cf-slider-occupancy"
              />
              <div className="flex justify-between text-xs text-[#0A2235]/50 font-inter">
                <span>50%</span>
                <span>95%</span>
              </div>
            </div>

            {/* Operating Cost */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="font-inter text-sm text-[#0A2235]/70 uppercase tracking-widest">
                  {language === 'es' ? 'Gastos operativos' : 'Operating costs'}
                </label>
                <span className="font-inter font-medium text-[#0A2235] text-lg">
                  {operatingCost[0]}%
                </span>
              </div>
              <Slider
                value={operatingCost}
                onValueChange={setOperatingCost}
                min={20}
                max={45}
                step={1}
                className="[&_[role=slider]]:bg-[#0A2235] [&_[role=slider]]:border-[#0A2235] [&_.relative]:bg-[#0A2235]/20 [&_[data-orientation=horizontal]>[data-orientation=horizontal]]:bg-[#2BBFBF]"
                data-testid="cf-slider-costs"
              />
              <div className="flex justify-between text-xs text-[#0A2235]/50 font-inter">
                <span>20%</span>
                <span>45%</span>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              {/* Net Monthly - Hero result */}
              <div className="bg-[#0A2235] p-8 text-center rounded-2xl" data-testid="result-net-monthly">
                <div className="flex items-center justify-center gap-2 text-[#FAFAF7]/60 mb-2">
                  <DollarSign className="w-5 h-5" />
                  <span className="font-inter text-sm uppercase tracking-widest">
                    {language === 'es' ? 'Ingreso neto mensual' : 'Net monthly income'}
                  </span>
                </div>
                <p className="font-playfair text-4xl md:text-5xl text-[#2BBFBF]">
                  {formatCurrency(calculations.netMonthly).usd}
                </p>
                <p className="font-inter text-[#FAFAF7]/50 text-sm mt-1">
                  {formatCurrency(calculations.netMonthly).mxn}
                </p>
                <p className="font-inter text-[#FAFAF7]/40 text-xs mt-2">
                  {language === 'es' ? 'Después de gastos operativos' : 'After operating expenses'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Annual Income */}
                <div className="bg-[#0A2235] p-6 text-center rounded-2xl" data-testid="result-annual">
                  <div className="flex items-center justify-center gap-2 text-[#FAFAF7]/60 mb-2">
                    <Calendar className="w-4 h-4" />
                    <span className="font-inter text-xs uppercase tracking-widest">
                      {language === 'es' ? 'Ingreso anual' : 'Annual income'}
                    </span>
                  </div>
                  <p className="font-playfair text-3xl md:text-4xl text-[#2BBFBF]">
                    {formatCurrency(calculations.netAnnual).usd}
                  </p>
                  <p className="font-inter text-[#FAFAF7]/50 text-xs mt-1">
                    {formatCurrency(calculations.netAnnual).mxn}
                  </p>
                </div>

                {/* Gross Monthly */}
                <div className="bg-[#0A2235] p-6 text-center rounded-2xl" data-testid="result-gross">
                  <div className="flex items-center justify-center gap-2 text-[#FAFAF7]/60 mb-2">
                    <TrendingUp className="w-4 h-4" />
                    <span className="font-inter text-xs uppercase tracking-widest">
                      {language === 'es' ? 'Bruto mensual' : 'Gross monthly'}
                    </span>
                  </div>
                  <p className="font-playfair text-3xl md:text-4xl text-[#7ECFCF]">
                    {formatCurrency(calculations.grossMonthly).usd}
                  </p>
                  <p className="font-inter text-[#FAFAF7]/50 text-xs mt-1">
                    {formatCurrency(calculations.grossMonthly).mxn}
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <Button
              asChild
              className="w-full bg-[#2BBFBF] hover:bg-[#7ECFCF] text-[#0A2235] font-medium rounded-full py-6 text-base transition-all duration-300 active:scale-95 group"
              data-testid="calculator-cta"
            >
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                {language === 'es' ? 'Quiero propiedades con este perfil' : 'I want properties with this profile'}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
